import { useState, useEffect, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, X, ShieldCheck } from "lucide-react";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [enums, setEnums] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    const [u, e] = await Promise.all([api.get("/users"), api.get("/meta/enums")]);
    setUsers(u.data); setEnums(e.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (payload, id) => {
    try {
      if (id) await api.put(`/users/${id}`, payload);
      else await api.post("/users", payload);
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><h1 className="font-head text-2xl font-extrabold">Users &amp; Roles</h1><p className="text-slate-500 text-sm mt-1">Role-based access with granular permission overrides.</p></div>
        <button onClick={() => setModal({ data: { role: "VIEW_ONLY", extra_permissions: [] } })} data-testid="user-add" className="h-9 px-4 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold flex items-center gap-2"><UserPlus size={16} /> Add User</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500"><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Permissions</th><th className="px-4 py-3">Active</th><th className="px-4 py-3 text-right">Edit</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100">
                <td className="px-4 py-3 font-semibold">{u.name}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 flex items-center gap-1 w-fit"><ShieldCheck size={12} />{u.role?.replace(/_/g, " ")}</span></td>
                <td className="px-4 py-3"><div className="flex flex-wrap gap-1 max-w-md">{u.permissions?.map((p) => <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{p}</span>)}</div></td>
                <td className="px-4 py-3">{u.active ? "Yes" : "No"}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => setModal({ data: u, id: u.id })} data-testid={`user-edit-${u.id}`} className="text-blue-700 text-sm font-medium">Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && <UserForm modal={modal} enums={enums} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

function UserForm({ modal, enums, onClose, onSave }) {
  const isEdit = !!modal.id;
  const [f, setF] = useState({ name: modal.data.name || "", email: modal.data.email || "", role: modal.data.role || "VIEW_ONLY", password: "", extra_permissions: modal.data.extra_permissions || [], active: modal.data.active ?? true });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const field = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 outline-none text-sm";
  const rolePerms = enums?.role_permissions?.[f.role] || [];

  const submit = () => {
    const payload = { name: f.name, role: f.role, extra_permissions: f.extra_permissions, active: f.active };
    if (!isEdit) { payload.email = f.email; payload.password = f.password; }
    else if (f.password) payload.password = f.password;
    onSave(payload, modal.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="user-form">
        <div className="flex items-center justify-between p-5 border-b"><h3 className="font-head font-bold text-lg">{isEdit ? "Edit" : "New"} User</h3><button onClick={onClose} className="text-slate-400"><X /></button></div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium">Name</label><input value={f.name} onChange={(e) => set("name", e.target.value)} className={field} data-testid="user-name" /></div>
            <div><label className="text-sm font-medium">Email</label><input value={f.email} onChange={(e) => set("email", e.target.value)} disabled={isEdit} className={`${field} disabled:bg-slate-100`} data-testid="user-email" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium">Role</label><select value={f.role} onChange={(e) => set("role", e.target.value)} className={field} data-testid="user-role">{enums?.roles.map((r) => <option key={r}>{r}</option>)}</select></div>
            <div><label className="text-sm font-medium">{isEdit ? "New Password (optional)" : "Password"}</label><input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} className={field} data-testid="user-password" /></div>
          </div>
          <div>
            <label className="text-sm font-medium">Extra Permissions (beyond role)</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {enums?.all_permissions.map((p) => {
                const inRole = rolePerms.includes(p);
                const checked = inRole || f.extra_permissions.includes(p);
                return (
                  <button key={p} disabled={inRole} onClick={() => set("extra_permissions", f.extra_permissions.includes(p) ? f.extra_permissions.filter((x) => x !== p) : [...f.extra_permissions, p])}
                    className={`text-xs px-2.5 py-1 rounded-lg border ${checked ? "bg-blue-600 text-white border-blue-600" : "border-slate-300 text-slate-600"} ${inRole ? "opacity-60 cursor-not-allowed" : ""}`}>{p}</button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Highlighted = granted. Role-inherited permissions can't be removed here.</p>
          </div>
          {isEdit && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>}
        </div>
        <div className="p-5 border-t flex justify-end gap-3"><button onClick={onClose} className="px-4 h-10 rounded-lg border text-sm">Cancel</button><button onClick={submit} data-testid="user-save" className="px-5 h-10 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold">Save</button></div>
      </div>
    </div>
  );
}
